import React from "react";
import { X, ExternalLink, KeyRound, CheckCircle2, Copy } from "lucide-react";

interface GoogleConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GoogleConfigModal: React.FC<GoogleConfigModalProps> = ({ isOpen, onClose }) => {
  const [copied, setCopied] = React.useState(false);

  if (!isOpen) return null;

  const exampleEnv = `VITE_GOOGLE_CLIENT_ID="votre_client_id.apps.googleusercontent.com"\nGOOGLE_CLIENT_ID="votre_client_id.apps.googleusercontent.com"`;

  const copyEnv = () => {
    navigator.clipboard.writeText(exampleEnv);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full p-6 space-y-5 shadow-2xl animate-in zoom-in-95 duration-200 text-slate-800">
        
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 text-blue-600 flex items-center justify-center shrink-0">
              <KeyRound className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Configuration Google Auth</h3>
              <p className="text-xs text-slate-500">Activez la connexion Google en 2 minutes</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Message explicatif */}
        <div className="bg-amber-50 border border-amber-200/80 rounded-xl p-3.5 text-xs text-amber-800 leading-relaxed">
          Le <strong>Google Client ID</strong> n'est pas encore renseigné dans votre configuration (<code className="font-mono bg-amber-100/60 px-1 py-0.5 rounded">.env.local</code>).
        </div>

        {/* Étapes */}
        <div className="space-y-3 text-xs text-slate-600">
          <div className="flex gap-2.5 items-start">
            <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 font-bold flex items-center justify-center shrink-0 text-[11px]">1</span>
            <p>
              Accédez à la <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5 font-medium">Google Cloud Console <ExternalLink className="w-3 h-3" /></a> et créez un <strong>ID Client OAuth 2.0</strong> (Type : <em>Application Web</em>).
            </p>
          </div>

          <div className="flex gap-2.5 items-start">
            <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 font-bold flex items-center justify-center shrink-0 text-[11px]">2</span>
            <div>
              <p>Ajoutez vos origines JavaScript autorisées :</p>
              <ul className="list-disc pl-4 mt-1 space-y-0.5 text-slate-500 font-mono text-[11px]">
                <li>http://localhost:3000</li>
                <li>https://votre-domaine.com (en production)</li>
              </ul>
            </div>
          </div>

          <div className="flex gap-2.5 items-start">
            <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 font-bold flex items-center justify-center shrink-0 text-[11px]">3</span>
            <p>
              Ajoutez l'identifiant dans votre fichier <strong>.env.local</strong> :
            </p>
          </div>
        </div>

        {/* Code snippet */}
        <div className="relative bg-slate-900 rounded-xl p-3 font-mono text-[11px] text-emerald-400 border border-slate-800">
          <button
            type="button"
            onClick={copyEnv}
            className="absolute top-2 right-2 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded-md flex items-center gap-1 transition-colors cursor-pointer"
          >
            {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copied ? "Copié" : "Copier"}</span>
          </button>
          <pre className="overflow-x-auto pr-16">{exampleEnv}</pre>
        </div>

        {/* Actions */}
        <div className="pt-2 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-4 py-2 rounded-xl transition-colors cursor-pointer"
          >
            Compris
          </button>
        </div>

      </div>
    </div>
  );
};
